import React, { useState } from 'react';
import { faqData } from '../data/mockData';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiScissors, FiWind, FiPlus, FiMinus } = FiIcons;

const AccordionItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(index === 0);
  return (
    <div className="border-b border-white/5 group">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center py-8 text-left focus:outline-none transition-all"
      >
        <h4 className={`font-serif text-xl md:text-2xl transition-colors duration-500 ${isOpen ? 'text-ember' : 'text-linen/80 group-hover:text-linen'}`}>
          {question}
        </h4>
        <div className={`p-2 rounded-full transition-all duration-500 ${isOpen ? 'bg-ember text-linen rotate-180' : 'bg-white/5 text-linen/40'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="w-4 h-4" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-10 pr-12">
              <p className="text-linen/50 leading-relaxed text-lg font-light">{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Care() {
  return (
    <main className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <header className="max-w-3xl mb-24">
          <span className="text-ember font-bold text-[10px] tracking-[0.4em] uppercase block mb-6">Preserving the Flame</span>
          <h1 className="font-serif text-5xl md:text-7xl text-linen mb-8 tracking-tight">The Ritual of Care</h1>
          <p className="text-linen/50 text-xl leading-relaxed font-light">
            An EmberVale candle is a living thing. To ensure the longest, cleanest burn and preserve the integrity of our fine fragrances, follow these essential rituals.
          </p>
        </header>

        {/* Ritual Detail Image */}
        <div className="mb-40 relative group">
          <div className="aspect-[21/9] overflow-hidden bg-smoke-lighter">
            <picture>
              <source srcSet="https://images.unsplash.com/photo-1605651202774-7d573fd3f12d?auto=format,compress&fit=crop&q=80&w=1600&fm=webp" type="image/webp" />
              <img 
                src="https://images.unsplash.com/photo-1605651202774-7d573fd3f12d?auto=format,compress&fit=crop&q=80&w=1600" 
                alt="A macro shot of a perfectly trimmed cotton wick and clear wax pool" 
                width="1600"
                height="685"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-1000"
              />
            </picture>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-smoke via-transparent to-transparent"></div>
          <div className="absolute bottom-8 left-8">
            <span className="text-ember font-bold text-[9px] tracking-[0.4em] uppercase">Visual Guide: The Ideal Burn</span>
          </div>
        </div>

        {/* Burn Guide Icons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1 px-1 mb-40 bg-white/5 border border-white/5">
          {[
            { 
              icon: FiClock, 
              title: 'The First Burn', 
              text: 'Allow the wax to melt completely to the edges (2-3 hours). This prevents tunneling and ensures a flat surface for life.'
            },
            { 
              icon: FiScissors, 
              title: 'Trim the Wick', 
              text: 'Before every light, trim the wick to 1/4 inch. A long wick causes smoke, soot, and an uneven flame.'
            },
            { 
              icon: FiWind, 
              title: 'The Environment', 
              text: 'Keep away from drafts, vents, or fans. Moving air causes the flame to flicker and produce black smoke.'
            }
          ].map((item, i) => (
            <div key={i} className="bg-smoke p-12 flex flex-col items-center text-center group hover:bg-smoke-light transition-colors duration-500">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-8 text-ember group-hover:scale-110 transition-transform duration-500">
                <SafeIcon icon={item.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl text-linen mb-4">{item.title}</h3>
              <p className="text-sm text-linen/40 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <section className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-linen">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-2">
            {faqData.map((faq, idx) => (
              <AccordionItem key={idx} index={idx} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Shipping & Returns Support */}
        <section className="mt-40 p-16 md:p-24 bg-smoke-light border border-white/5 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-ember" />
          <h3 className="font-serif text-3xl md:text-4xl text-linen mb-6 italic">Still have questions?</h3>
          <p className="text-linen/50 text-lg max-w-2xl mx-auto mb-12">
            Our concierge team is available to assist with scent selection, shipping inquiries, or wholesale opportunities.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a href="mailto:support@embervale.com" className="px-12 py-5 bg-linen text-smoke text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-ember hover:text-linen transition-all duration-500">
              Email Concierge
            </a>
            <button className="px-12 py-5 border border-white/10 text-linen text-[11px] font-bold uppercase tracking-[0.2em] hover:border-linen transition-all duration-500">
              Shipping Policies
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}